import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Download, ArrowLeft, Calendar, User } from 'lucide-react';
import { dischargeSummaryService } from '../../lib/services.ipd';
import { admissionService } from '../../lib/services.ipd';
import type { DischargeSummary, Admission } from '../../lib/services.ipd';
import { downloadDischargeSummary } from '../../lib/pdf';
import toast from 'react-hot-toast';

export default function DischargeSummaryViewer() {
  const { summaryId } = useParams<{ summaryId: string }>();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DischargeSummary | null>(null);
  const [admission, setAdmission] = useState<Admission | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSummary();
  }, [summaryId]);

  const loadSummary = async () => {
    if (!summaryId) return;
    
    try {
      const summaryData = await dischargeSummaryService.getByAdmission(summaryId);
      if (summaryData) {
        setSummary(summaryData);
        const admissionData = await admissionService.getById(summaryData.admission_id);
        setAdmission(admissionData);
      }
    } catch (error) {
      console.error('Failed to load discharge summary:', error);
      toast.error('Failed to load discharge summary');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!summary || !admission) return;
    
    const summaryData = {
      summaryNo: summary.summary_no,
      patient: admission.patient,
      admission: {
        admission_date: admission.admission_date,
        discharge_date: summary.discharge_date
      },
      doctor: admission.doctor,
      admissionDiagnosis: summary.admission_diagnosis,
      finalDiagnosis: summary.final_diagnosis,
      treatmentSummary: summary.treatment_summary,
      proceduresPerformed: summary.procedures_performed,
      medicationsPrescribed: summary.medications_prescribed,
      followUpInstructions: summary.follow_up_instructions,
      dietRestrictions: summary.diet_restrictions,
      activityRestrictions: summary.activity_restrictions,
      followUpDate: summary.follow_up_date
    };
    
    downloadDischargeSummary(summaryData);
    toast.success('Discharge summary downloaded');
  };

  if (loading) {
    return <div className="p-6">Loading discharge summary...</div>;
  }

  if (!summary || !admission) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Discharge Summary Not Found</h2>
          <p className="text-gray-600 mb-6">The discharge summary you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/ipd')}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Back to Admissions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate(`/ipd/${admission.id}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admission
        </button>
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="border-b pb-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Discharge Summary</h1>
          <p className="text-gray-600">Summary No: {summary.summary_no}</p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Patient Information</h3>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{admission.patient?.name}</span>
              </div>
              {admission.patient?.date_of_birth && (
                <p className="text-sm text-gray-600">
                  DOB: {new Date(admission.patient.date_of_birth).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Admission Details</h3>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm">
                  Admitted: {new Date(admission.admission_date).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Discharged: {new Date(summary.discharge_date).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-600">
                Doctor: {admission.doctor?.name}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {summary.admission_diagnosis && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Admission Diagnosis</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.admission_diagnosis}</p>
            </div>
          )}

          {summary.final_diagnosis && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Final Diagnosis</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.final_diagnosis}</p>
            </div>
          )}

          {summary.treatment_summary && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Treatment Summary</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.treatment_summary}</p>
            </div>
          )}

          {summary.procedures_performed && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Procedures Performed</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.procedures_performed}</p>
            </div>
          )}

          {summary.medications_prescribed && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Medications Prescribed</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.medications_prescribed}</p>
            </div>
          )}

          {summary.follow_up_instructions && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Follow-up Instructions</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.follow_up_instructions}</p>
            </div>
          )}

          {summary.diet_restrictions && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Diet Restrictions</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.diet_restrictions}</p>
            </div>
          )}

          {summary.activity_restrictions && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Activity Restrictions</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{summary.activity_restrictions}</p>
            </div>
          )}

          {summary.follow_up_date && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Follow-up Appointment</h3>
              <p className="text-gray-700">
                Scheduled for: {new Date(summary.follow_up_date).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 pt-6 border-t">
          <p className="text-sm text-gray-500 text-center">
            This discharge summary can be verified at: {window.location.origin}/verify/discharge/{summary.summary_no}
          </p>
        </div>
      </div>
    </div>
  );
}
